import React, { useCallback, useEffect, useState } from "react";
import {
  Table, Button, Modal, Form, Input, Select, Tag, Space, Popconfirm, message,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  fetchTeamsApi, createTeamApi, updateTeamApi, deleteTeamApi,
} from "../../../../apiservice/crm";

const TeamsManager = ({ users = [] }) => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchTeamsApi();
      setTeams(res.data || []);
    } catch {
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const userOptions = users.map((u) => ({ label: u.full_name, value: u.id }));

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (team) => {
    setEditing(team);
    form.setFieldsValue({
      name: team.name,
      team_lead_id: team.team_lead_id,
      member_ids: team.members.map((m) => m.id),
    });
    setModalOpen(true);
  };

  const submit = async () => {
    const values = await form.validateFields();

    // Changing the lead drops the old lead off the team entirely (they
    // aren't auto-added back as a plain member) -- confirm before saving.
    if (editing && values.team_lead_id !== editing.team_lead_id) {
      await new Promise((resolve, reject) => {
        Modal.confirm({
          title: "Change team lead?",
          content: `${editing.team_lead_name || "The current lead"} will leave this team entirely, unless you also add them back as a member below.`,
          okText: "Yes, change",
          onOk: resolve,
          onCancel: reject,
        });
      }).catch(() => Promise.reject(new Error("cancelled")));
    }

    setSaving(true);
    try {
      if (editing) {
        await updateTeamApi({ id: editing.id, ...values });
        message.success("Team updated");
      } else {
        await createTeamApi(values);
        message.success("Team created");
      }
      setModalOpen(false);
      load();
    } catch (err) {
      if (err?.message === "cancelled") return;
      message.error(err?.response?.data?.message || "Failed to save team");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (team) => {
    try {
      await deleteTeamApi({ id: team.id });
      message.success("Team deleted");
      load();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to delete team");
    }
  };

  const columns = [
    { title: "Team", dataIndex: "name" },
    { title: "Team Lead", dataIndex: "team_lead_name", render: (v) => v || "-" },
    {
      title: "Members",
      dataIndex: "members",
      render: (members) =>
        members?.length
          ? members.map((m) => <Tag key={m.id}>{m.full_name}</Tag>)
          : <span style={{ color: "#999" }}>No members yet</span>,
    },
    {
      title: "Actions",
      width: 110,
      render: (_, team) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(team)} />
          <Popconfirm
            title="Delete this team?"
            description="Members become unassigned from any team and can be added elsewhere."
            onConfirm={() => remove(team)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 12, display: "flex", justifyContent: "flex-end" }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          New Team
        </Button>
      </div>

      <Table
        rowKey="id"
        dataSource={teams}
        columns={columns}
        loading={loading}
        pagination={false}
      />

      <Modal
        title={editing ? "Edit Team" : "New Team"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submit}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Team Name"
            rules={[{ required: true, message: "Team name is required" }]}
          >
            <Input placeholder="e.g. North Region Sales" />
          </Form.Item>

          <Form.Item
            name="team_lead_id"
            label="Team Lead"
            rules={[{ required: true, message: "Pick a team lead" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={userOptions}
              placeholder="Select team lead"
            />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.team_lead_id !== cur.team_lead_id}
          >
            {({ getFieldValue }) => {
              const leadId = getFieldValue("team_lead_id");
              return (
                <Form.Item name="member_ids" label="Members">
                  <Select
                    mode="multiple"
                    showSearch
                    optionFilterProp="label"
                    options={userOptions.filter((o) => o.value !== leadId)}
                    placeholder="Select team members"
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TeamsManager;
